import { MGPFallible, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord, CoordFailure } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { ConspirateursFailure } from './ConspirateursFailure';
import { ConspirateursMove, ConspirateursMoveJump } from './ConspirateursMove';
import { ConspirateursState } from './ConspirateursState';
export class ConspirateursNode extends GameNode {
}
export class ConspirateursRules extends Rules {
    static NUMBER_OF_PIECES = 40;
    static singleton = MGPOptional.empty();
    static get() {
        if (ConspirateursRules.singleton.isAbsent()) {
            ConspirateursRules.singleton = MGPOptional.of(new ConspirateursRules());
        }
        return ConspirateursRules.singleton.get();
    }
    getInitialState() {
        const board = TableUtils.create(ConspirateursState.WIDTH, ConspirateursState.HEIGHT, PlayerOrNone.NONE);
        return new ConspirateursState(board, 0);
    }
    applyLegalMove(move, state, _config, _info) {
        const updatedBoard = state.getCopiedBoard();
        if (ConspirateursMove.isDrop(move)) {
            updatedBoard[move.coord.y][move.coord.x] = state.getCurrentPlayer();
        }
        else if (ConspirateursMove.isSimple(move)) {
            updatedBoard[move.getStart().y][move.getStart().x] = PlayerOrNone.NONE;
            updatedBoard[move.getEnd().y][move.getEnd().x] = state.getCurrentPlayer();
        }
        else {
            const start = move.getStartingCoord();
            const end = move.getEndingCoord();
            updatedBoard[start.y][start.x] = PlayerOrNone.NONE;
            updatedBoard[end.y][end.x] = state.getCurrentPlayer();
        }
        return new ConspirateursState(updatedBoard, state.turn + 1);
    }
    isLegal(move, state) {
        if (ConspirateursMove.isDrop(move)) {
            return this.dropLegality(move, state);
        }
        else if (ConspirateursMove.isSimple(move)) {
            return this.simpleMoveLegality(move, state);
        }
        else {
            return this.jumpLegality(move, state);
        }
    }
    dropLegality(move, state) {
        Utils.assert(state.isOnBoard(move.coord), 'Move out of board');
        if (ConspirateursRules.NUMBER_OF_PIECES <= state.turn) {
            return MGPValidation.failure(ConspirateursFailure.CANNOT_DROP_WHEN_OUT_OF_PIECE());
        }
        if (state.getPieceAt(move.coord).isPlayer()) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        if (state.isCentralZone(move.coord) === false) {
            return MGPValidation.failure(ConspirateursFailure.MUST_DROP_IN_CENTRAL_ZONE());
        }
        return MGPValidation.SUCCESS;
    }
    simpleMoveLegality(move, state) {
        const startInRange = state.isOnBoard(move.getStart());
        const endInRange = state.isOnBoard(move.getEnd());
        Utils.assert(startInRange && endInRange, 'Move out of board');
        if (state.turn < ConspirateursRules.NUMBER_OF_PIECES) {
            return MGPValidation.failure(ConspirateursFailure.CANNOT_MOVE_BEFORE_DROPPING_ALL_PIECES());
        }
        const startPiece = state.getPieceAt(move.getStart());
        if (startPiece.isNone()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (startPiece === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        if (state.getPieceAt(move.getEnd()).isPlayer()) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        return MGPValidation.SUCCESS;
    }
    jumpLegality(move, state) {
        for (const coord of move.coords) {
            if (state.isNotOnBoard(coord)) {
                return MGPFallible.failure(CoordFailure.OUT_OF_RANGE(coord));
            }
        }
        if (state.turn < ConspirateursRules.NUMBER_OF_PIECES) {
            return MGPValidation.failure(ConspirateursFailure.CANNOT_MOVE_BEFORE_DROPPING_ALL_PIECES());
        }
        const startPiece = state.getPieceAt(move.getStartingCoord());
        if (startPiece.isNone()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (startPiece === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        for (const jumpedOver of move.getJumpedOverCoords()) {
            if (state.getPieceAt(jumpedOver).isNone()) {
                return MGPValidation.failure(ConspirateursFailure.MUST_JUMP_OVER_PIECES());
            }
        }
        for (const landing of move.getLandingCoords()) {
            if (state.getPieceAt(landing).isPlayer()) {
                return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
            }
        }
        return MGPValidation.SUCCESS;
    }
    jumpTargetsFrom(state, start) {
        const targets = [
            new Coord(start.x + 2, start.y),
            new Coord(start.x - 2, start.y),
            new Coord(start.x, start.y + 2),
            new Coord(start.x, start.y - 2),
            new Coord(start.x + 2, start.y + 2),
            new Coord(start.x + 2, start.y - 2),
            new Coord(start.x - 2, start.y + 2),
            new Coord(start.x - 2, start.y - 2),
        ];
        const validTargets = [];
        for (const target of targets) {
            if (state.isOnBoard(target)) {
                const move = ConspirateursMoveJump.from([start, target]);
                if (move.isSuccess()) {
                    validTargets.push(target);
                }
            }
        }
        return validTargets;
    }
    nextJumps(jump, state) {
        const ending = jump.getEndingCoord();
        const nextJumps = [];
        for (const target of this.jumpTargetsFrom(state, ending)) {
            const move = jump.addJump(target);
            if (move.isSuccess() && this.jumpLegality(move.get(), state).isSuccess()) {
                nextJumps.push(move.get());
            }
        }
        return nextJumps;
    }
    jumpHasPossibleNextTargets(jump, state) {
        return this.nextJumps(jump, state).length > 0;
    }
    getGameStatus(node) {
        const protectedPieces = this.getProtectedPieces(node.gameState);
        for (const player of Player.PLAYERS) {
            if (protectedPieces.get(player) === 20) {
                return GameStatus.getVictory(player);
            }
        }
        return GameStatus.ONGOING;
    }
    getProtectedPieces(state) {
        const protectedPieces = PlayerNumberMap.of(0, 0);
        for (const shelter of ConspirateursState.ALL_SHELTERS) {
            const content = state.getPieceAt(shelter);
            if (content.isPlayer()) {
                protectedPieces.add(content, 1);
            }
        }
        return protectedPieces;
    }
}
